import { Box, FormControl } from '@mui/material'

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs, { Dayjs } from 'dayjs'
import { ISO_DATE_TIME_FORMAT_Z } from '../../utils/DateAndTimeUtils'
import { ReactElement, useEffect, useMemo, useState } from 'react'
import {
    DateRange,
    DateTimeRangePicker,
    PickersActionBar,
    PickersActionBarProps,
    PickersShortcutsItem,
    renderTimeViewClock,
} from '@mui/x-date-pickers-pro'
import { useAppSelector } from '../../redux/hook'
import { useUserDateFormat } from '../../hooks/useUserDateFormat'

interface DateTimeRangeInputProps {
    id: string
    label: string
    placehodlerLabel?: string
    value: any[]
    onChange: any
    disabled?: boolean
    clearable?: boolean
    maxDate?: Dayjs
    minDate?: Dayjs
}

const DateTimeRangeInput = (props: DateTimeRangeInputProps): ReactElement => {
    const {
        id,
        label,
        placehodlerLabel,
        value,
        onChange,
        disabled,
        clearable,
        maxDate,
        minDate,
    } = props

    const userLocalization = useAppSelector(
        (state) => state.user.user?.preferences?.localization,
    )
    const userTimeZone = useMemo(
        () => userLocalization?.timeZone || dayjs.tz.guess(),
        [userLocalization],
    )
    const { hasMeridiem, userPreferredDateFormat } = useUserDateFormat()
    const [val, setVal] = useState<DateRange<dayjs.Dayjs>>([null, null]) //final value, updated only when click on "ok"
    const [temVal, setTemVal] = useState<DateRange<dayjs.Dayjs>>([null, null]) //temporary value, update every time when user select date
    const [nextClick, setNextClick] = useState<boolean>(false)
    const [open, setOpen] = useState<boolean>(false)

    //used this to show placeholder as lable when there is no value
    const [focused, setFocused] = useState(false)

    const shortcutsItems: PickersShortcutsItem<DateRange<Dayjs>>[] = [
        {
            label: 'Today',
            getValue: () => {
                const todayStartTime = dayjs().tz(userTimeZone)
                const todayEndTime = dayjs().tz(userTimeZone)
                return [todayStartTime, todayEndTime]
            },
        },
    ]

    useEffect(() => {
        if (value && value.length > 0) {
            setVal([
                value[0] ? dayjs(value[0]).tz(userTimeZone) : null,
                value[1] ? dayjs(value[1]).tz(userTimeZone) : null,
            ])
            setTemVal([
                value[0] ? dayjs(value[0]).tz(userTimeZone) : null,
                value[1] ? dayjs(value[1]).tz(userTimeZone) : null,
            ])
        }
    }, [value])
    const handleChangeDateRange = (newValue: DateRange<dayjs.Dayjs>) => {
        const range: DateRange<dayjs.Dayjs> = [
            newValue[0]?.tz(userTimeZone, true) || null,
            newValue[1]?.tz(userTimeZone, true) || null,
        ]
        setTemVal(range)
    }

    const onClickOk = () => {
        handleSubmitDateRange(temVal)
    }

    const handleCloseDateRange = () => {
        handleSubmitDateRange()
        setOpen(false)
    }

    const handleSubmitDateRange = (newValue?: any) => {
        const updatedVal = newValue ?? val

        const updateNewValue = [
            updatedVal[0]
                ? dayjs(updatedVal[0]).utc().format(ISO_DATE_TIME_FORMAT_Z)
                : null,
            updatedVal[1]
                ? dayjs(updatedVal[1]).utc().format(ISO_DATE_TIME_FORMAT_Z)
                : null,
        ]
        onChange(id, updateNewValue)
    }

    const onClearDateRange = () => {
        onChange(id, [null, null])
    }
    const shouldEnableOkorNext = nextClick ? temVal[0] && temVal[1] : temVal[0]
    return (
        <Box sx={{ width: '100%' }}>
            <FormControl fullWidth disabled={!!disabled} size="small">
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DateTimeRangePicker
                        value={temVal}
                        timezone={userTimeZone}
                        open={open}
                        onOpen={() => setOpen(true)}
                        onClose={() => {
                            handleCloseDateRange()
                        }}
                        viewRenderers={{
                            hours: renderTimeViewClock,
                            minutes: renderTimeViewClock,
                        }}
                        onChange={(newValue) => handleChangeDateRange(newValue)}
                        onAccept={(newValue) => handleChangeDateRange(newValue)}
                        closeOnSelect={false}
                        label={
                            val[0] || val[1] || focused || open
                                ? label
                                : placehodlerLabel || ''
                        }
                        disableHighlightToday
                        ampm={hasMeridiem}
                        calendars={1}
                        slotProps={{
                            textField: {
                                size: 'small',
                                InputProps: { readOnly: true }, // disable typing
                                onChange: () => {},
                                onClick: (e) => {
                                    // let MUI handle open internally
                                    const calendarButton =
                                        e.currentTarget.querySelector(
                                            "button[aria-label='Choose range']",
                                        ) as HTMLElement

                                    calendarButton?.click()
                                },
                                onFocus: () => setFocused(true),
                                onBlur: () => setFocused(false),
                                sx: {
                                    // bgcolor: '#fff',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    '.clearButton': {
                                        padding: '0px',
                                    },
                                    '& label': {
                                        color: '#000000de',
                                    },
                                },
                            },
                            field: {
                                clearable,
                                onClear: () => {
                                    onClearDateRange()
                                },
                            },
                            desktopPaper: {
                                sx: {
                                    '& .MuiDayCalendar-slideTransition': {
                                        minHeight: '215px',
                                    },
                                },
                            },
                            actionBar: {
                                actions: ['cancel', 'nextOrAccept'],
                                sx: {
                                    paddingTop: '10px',
                                    paddingBottom: '10px',
                                    '& .MuiButton-root:nth-of-type(2)':
                                        shouldEnableOkorNext
                                            ? {}
                                            : {
                                                  color: '#9e9e9e',
                                                  cursor: 'not-allowed',
                                                  pointerEvents: 'none',
                                              },
                                },
                            },
                            shortcuts: {
                                items: shortcutsItems,
                                changeImportance: 'set',
                                sx: {
                                    position: 'absolute',
                                    bottom: '-3px',
                                    left: '0',
                                    '& div': {
                                        fontSize: '13px',
                                    },
                                },
                            },
                        }}
                        slots={{
                            actionBar: (props: PickersActionBarProps) => (
                                <PickersActionBar
                                    {...props}
                                    onClick={(e) => {
                                        const target = (e.target as HTMLElement)
                                            .textContent
                                        if (
                                            target === 'Cancel' ||
                                            target === 'CancelNext'
                                        ) {
                                            handleSubmitDateRange()
                                        } else if (target === 'Next') {
                                            setNextClick(true)
                                        } else {
                                            onClickOk()
                                        }
                                    }}
                                />
                            ),
                        }}
                        localeText={{ start: 'Check-in', end: 'Check-out' }}
                        maxDate={maxDate ?? undefined}
                        minDate={minDate ?? undefined}
                        format={userPreferredDateFormat}
                    />
                </LocalizationProvider>
            </FormControl>
        </Box>
    )
}
export default DateTimeRangeInput
